/**
 * Typed wrappers around the /api/me/profile BFF surface.
 * Mirrors the Zod schemas declared in apps/api/src/routes/profile.ts
 * (unified DIAN verification flow).
 */
import { fetchApiWithAuth } from "@/lib/api";

// ── Shared types ──────────────────────────────────────────────────────────────

export type IdentificationType = "NIT" | "CC" | "CE" | "TI" | "PP" | "PPT";

export type ProfileIdentification = {
  type: IdentificationType | null;
  number: string | null;
  dv: string | null;
  verified: boolean;
  verifiedAt: string | null;
  verifiedVia: string | null;
  /** DIAN-canonical legal name, snapshot at verification time. */
  legalNameDian: string | null;
  /** DIAN-registered email, snapshot at verification time. */
  emailDian: string | null;
};

export type ProfileData = {
  displayName: string | null;
  email: string | null;
  locale: string;
  timezone: string;
  monetaryDecimalPlaces: number;
  identification: ProfileIdentification;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
};

export type ProfileCompletion = {
  score: number;          // 0–100
  missing: string[];      // ['displayName', 'dianVerified']
};

export type ProfileGetResponse = {
  user: {
    id: string;
    phone: string;
    role: "admin" | "user";
    defaultTeamId: string | null;
  };
  profile: ProfileData | null;
  completion: ProfileCompletion;
};

export type ProfileUpdateInput = {
  displayName?: string;
  locale?: string;
  timezone?: string;
  monetaryDecimalPlaces?: number;
};

export type ProfileUpdateResponse = {
  userId: string;
  phone: string;
  displayName: string | null;
  email: string | null;
  locale: string;
  timezone: string;
  monetaryDecimalPlaces: number;
  createdAt: string;
  updatedAt: string;
};

// ── Unified DIAN verification ────────────────────────────────────────────────

export type VerificationStartInput = {
  identificationType: IdentificationType;
  identificationNumber: string;
  identificationDv?: string;
};

export type VerificationStartResponse =
  | {
      found: true;
      challengeId: string;
      expiresAt: string;
      masked: { name: string; email?: string };
    }
  | {
      found: true;
      reason: "noEmail";
      masked: { name: string; email?: undefined };
      message: string;
    }
  | {
      found: false;
      reason: "notFound";
      message: string;
    };

export type VerificationCompleteInput = {
  challengeId: string;
  code: string;     // 6 digits
};

export type VerificationCompleteResponse = {
  verified: boolean;
  profile: ProfileData | null;
};

// ── API functions ─────────────────────────────────────────────────────────────

export async function getProfile(): Promise<ProfileGetResponse> {
  return fetchApiWithAuth<ProfileGetResponse>("/api/me/profile");
}

export async function updateProfile(
  input: ProfileUpdateInput
): Promise<ProfileUpdateResponse> {
  return fetchApiWithAuth<ProfileUpdateResponse>("/api/me/profile", {
    method: "PUT",
    body: input,
  });
}

export async function startVerification(
  input: VerificationStartInput
): Promise<VerificationStartResponse> {
  return fetchApiWithAuth<VerificationStartResponse>(
    "/api/me/profile/verification/start",
    { method: "POST", body: input }
  );
}

export async function completeVerification(
  input: VerificationCompleteInput
): Promise<VerificationCompleteResponse> {
  return fetchApiWithAuth<VerificationCompleteResponse>(
    "/api/me/profile/verification/complete",
    { method: "POST", body: input }
  );
}
